class NovaSelect extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('select');
    if (!this.select || this.button) return;

    this.optionButtons = [];
    this.render();
    this.select.addEventListener('change', this.sync);
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onDocumentKeyDown);
  }

  disconnectedCallback() {
    this.select?.removeEventListener('change', this.sync);
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onDocumentKeyDown);
    this.list?.removeEventListener('scroll', this.onListScroll);
    clearTimeout(this.scrollbarTimer);
  }

  render() {
    this.classList.add('nova-select');
    this.select.classList.add('nova-select__native');
    this.select.tabIndex = -1;

    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.className = 'nova-select__button';
    this.button.setAttribute('aria-haspopup', 'listbox');
    this.button.setAttribute('aria-expanded', 'false');
    this.button.setAttribute('aria-label', this.select.getAttribute('aria-label') || '');
    this.button.addEventListener('click', this.toggle);
    this.button.addEventListener('keydown', this.onButtonKeyDown);

    this.value = document.createElement('span');
    this.value.className = 'nova-select__value';

    this.caret = document.createElement('span');
    this.caret.className = 'nova-select__caret';
    this.caret.setAttribute('aria-hidden', 'true');

    this.button.append(this.value, this.caret);

    this.list = document.createElement('div');
    this.list.className = 'nova-select__list';
    this.list.setAttribute('role', 'listbox');
    this.list.setAttribute('tabindex', '-1');
    this.list.addEventListener('scroll', this.onListScroll, { passive: true });

    Array.from(this.select.options).forEach((option) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'nova-select__option';
      item.setAttribute('role', 'option');
      item.dataset.value = option.value;
      item.textContent = option.textContent.trim();
      item.addEventListener('click', () => this.choose(option.value));
      item.addEventListener('keydown', this.onOptionKeyDown);
      this.optionButtons.push(item);
      this.list.append(item);
    });

    this.append(this.button, this.list);
    this.sync();
  }

  sync = () => {
    const selectedOption = this.select.options[this.select.selectedIndex];
    this.value.textContent = selectedOption?.textContent.trim() || '';

    this.optionButtons.forEach((optionButton) => {
      const isSelected = optionButton.dataset.value === this.select.value;
      optionButton.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      optionButton.tabIndex = isSelected ? 0 : -1;
    });
  };

  toggle = () => {
    this.isOpen() ? this.close() : this.open();
  };

  open() {
    document.querySelectorAll('nova-select[open]').forEach((select) => {
      if (select !== this && typeof select.close === 'function') select.close();
    });

    this.setAttribute('open', '');
    this.button.setAttribute('aria-expanded', 'true');
    this.currentOption()?.focus({ preventScroll: true });
  }

  close() {
    this.removeAttribute('open');
    this.button.setAttribute('aria-expanded', 'false');
  }

  isOpen() {
    return this.hasAttribute('open');
  }

  choose(value) {
    if (this.select.value !== value) {
      this.select.value = value;
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.close();
    this.button.focus({ preventScroll: true });
  }

  currentOption() {
    return this.optionButtons.find((optionButton) => optionButton.dataset.value === this.select.value) || this.optionButtons[0];
  }

  moveFocus(direction) {
    const currentIndex = this.optionButtons.indexOf(document.activeElement);
    const fallbackIndex = Math.max(0, this.optionButtons.indexOf(this.currentOption()));
    const nextIndex =
      currentIndex >= 0
        ? (currentIndex + direction + this.optionButtons.length) % this.optionButtons.length
        : fallbackIndex;

    this.optionButtons[nextIndex]?.focus({ preventScroll: true });
  }

  onButtonKeyDown = (event) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    this.open();
  };

  onOptionKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.moveFocus(1);
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.moveFocus(-1);
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.choose(event.currentTarget.dataset.value);
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      this.button.focus({ preventScroll: true });
    }
  };

  onDocumentClick = (event) => {
    if (!this.contains(event.target)) this.close();
  };

  onDocumentKeyDown = (event) => {
    if (event.key === 'Escape') this.close();
  };

  onListScroll = () => {
    this.list.classList.add('is-scrolling');
    clearTimeout(this.scrollbarTimer);
    this.scrollbarTimer = setTimeout(() => this.list?.classList.remove('is-scrolling'), 700);
  };
}

if (!customElements.get('nova-select')) {
  customElements.define('nova-select', NovaSelect);
}
